import React, { ReactElement, useContext } from 'react'

import Heading from '../../Common/Components/Heading'
import Fieldset from '../../Common/Components/Fieldset'
import { Select, SelectOptions } from '../../Common/Components/Selects'
import FormRow from '../../Common/Components/FormRow'
import {
    PAPERSIZE_A4_OPTION_VALUE,
    PAPERSIZE_LEGAL_OPTION_VALUE,
    PAPERSIZE_LETTER_OPTION_VALUE
} from '../../Common/Constants/PaperSizes'
import { LOCALE_EN_GB, LOCALE_EN_US } from '../../Common/Constants/Locale'

import { LanguageContext } from '../../Common/Contexts'

import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { Format as FormatLanguage } from '../../../../i18n'
import {
    SCHEDULEREPORT_FORMAT_TYPE_CSV,
    SCHEDULEREPORT_FORMAT_TYPE_PDF,
    SCHEDULEREPORT_FORMAT_TYPE_RTF,
    SCHEDULEREPORT_FORMAT_TYPE_XLS
} from './Constants'
import { HeadingLevel } from '../../Common/Components/Heading/Heading'

/**
 * Component properties type.
 */
type Props = {
    targetRptFormat: number
    setTargetRptFormat: (newValue: number) => void
    useLanguage: string | undefined
    setUseLanguage: (newValue: string) => void
    paperSize: string | undefined
    setPaperSize: (newValue: string) => void
}

/**
 * Format section in Scheduling tab of report configuration modal window.
 *
 * @param targetRptFormat Report property.
 * @param setTargetRptFormat Setter for corresponding report property.
 * @param useLanguage Report property.
 * @param setUseLanguage Setter for corresponding report property.
 * @param paperSize Report property.
 * @param setPaperSize Setter for corresponding report property.
 */
const Format: React.FC<Props> = ({
    targetRptFormat,
    setTargetRptFormat,
    useLanguage,
    setUseLanguage,
    paperSize,
    setPaperSize
}: Props): ReactElement => {
    const language = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle('Format')

    const targetRptFormats: SelectOptions<number> = [
        {
            value: SCHEDULEREPORT_FORMAT_TYPE_PDF,
            label: language.get(FormatLanguage.TargetreportformatPdfOptionLabel)
        },
        {
            value: SCHEDULEREPORT_FORMAT_TYPE_RTF,
            label: language.get(FormatLanguage.TargetreportformatRtfOptionLabel)
        },
        {
            value: SCHEDULEREPORT_FORMAT_TYPE_CSV,
            label: language.get(FormatLanguage.TargetreportformatCsvOptionLabel)
        },
        {
            value: SCHEDULEREPORT_FORMAT_TYPE_XLS,
            label: language.get(FormatLanguage.TargetreportformatXlsOptionLabel)
        }
    ]

    const formatLanguages: SelectOptions<string> = [
        {
            value: LOCALE_EN_GB,
            label: language.get(FormatLanguage.UselanguageEngbOptionLabel)
        },
        {
            value: LOCALE_EN_US,
            label: language.get(FormatLanguage.UselanguageEnusOptionLabel)
        }
    ]

    const paperSizes: SelectOptions<string> = [
        {
            value: PAPERSIZE_A4_OPTION_VALUE,
            label: language.get(FormatLanguage.PapersizeA4OptionLabel)
        },
        {
            value: PAPERSIZE_LEGAL_OPTION_VALUE,
            label: language.get(FormatLanguage.PapersizeLegalOptionLabel)
        },
        {
            value: PAPERSIZE_LETTER_OPTION_VALUE,
            label: language.get(FormatLanguage.PapersizeLetterOptionLabel)
        }
    ]

    return (
        <Fieldset className='scheduling-tab-format'>
            <Heading level={HeadingLevel.ONE}>{language.get(FormatLanguage.Format_SectionHeader)}</Heading>
            <FormRow isInline>
                <Select
                    label={language.get(FormatLanguage.TargetreportformatLabel)}
                    value={targetRptFormat}
                    handleChange={setTargetRptFormat}
                    options={targetRptFormats}
                />
            </FormRow>
            <FormRow isInline>
                <Select
                    label={language.get(FormatLanguage.UselanguageLabel)}
                    value={useLanguage}
                    handleChange={setUseLanguage}
                    options={formatLanguages}
                />
            </FormRow>
            <FormRow isInline>
                <Select
                    label={language.get(FormatLanguage.PapersizeLabel)}
                    value={paperSize}
                    handleChange={setPaperSize}
                    options={paperSizes}
                />
            </FormRow>
        </Fieldset>
    )
}

export default Format
